import Head from 'next/head'
import { useContext, useRef } from 'react'
import Swiper from 'react-id-swiper'
import styled, { ThemeContext } from 'styled-components'

import ExtraLargeHeader from 'components/ExtraLargeHeader'
import { LeftChevron, RightChevron } from 'components/SVG/Chevron'
import Spacer from 'components/Spacer'

const CUSTOM_BREAKPOINT = 1000
const S3_TOUR_URL = `${process.env.CDN_BASE_URL}/q_auto/amazon/assets/virtual-tour`

const MaxWidthContainer = styled.div`
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
  margin: 0 auto;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    padding: 0 30px;
  }
`

interface IReverseProps {
  reverse?: boolean
}
const Row = styled.div<IReverseProps>`
  display: flex;
  flex-direction: ${props => `column${props.reverse ? '-reverse' : ''}`};
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    flex-direction: row;
  }
`

const FlexItem = styled.div`
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    flex-basis: 50vw;
  }
`

const TextContainer = styled.div`
  padding: 50px ${props => props.theme.metrics.gutterPhone}px 0;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 50px;
  }
`

const DownloadText = styled.div`
  font-style: normal;
  font-weight: 300;
  font-size: 22px;
  line-height: 32px;
  color: ${props => props.theme.colors.black};
  font-weight: 800;
`

const DownloadButtonContainer = styled.div`
  display: flex;
`

const DownloadImage = styled.img`
  height: 40px;
`

const StyledText = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${props => props.theme.colors.black};
`

const StyledParagraph = styled.p`
  margin: 0;
  padding: 0;

  padding-bottom: 30px;
  &:last-child {
    padding-bottom: 0;
  }
`

const StyledAnchor = styled.a`
  color: ${props => props.theme.colors.purple};
  text-decoration: underline;
  word-break: break-all;

  cursor: pointer;
`

const RowHeader = styled.h2`
  margin: 0;
  font-style: normal;
  font-weight: 300;
  font-size: 52px;
  line-height: 52px;
  color: ${props => props.theme.colors.black};
`

interface IImgSrcProps {
  imgSrc: string
}
const Image = styled.div<IImgSrcProps>`
  background: center / cover url(${props => props.imgSrc});
  width: 100%;
  height: 50vw;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    height: 488px;
  }
`

const ImageCard = styled.div<IImgSrcProps>`
  background: center / contain no-repeat url(${props => props.imgSrc});
  width: 100%;
  height: 350px;
  margin-bottom: 25px;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    height: 488px;
  }
`

const SwiperContainer = styled.div`
  height: 100%;
  width: 100%;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    width: 594px;
  }
`

// Custom swiper components
interface IOrientationProps {
  left?: boolean
}
const SwiperButtonContainer = styled.div<IOrientationProps>`
  display: none;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: block;
    position: absolute;
    top: calc(50% - 22.5px);
    ${props => `${props.left ? 'left' : 'right'}: 35px;`}
    z-index: 10000;
  }
`

const Circle = styled.div`
  height: 45px;
  width: 45px;
  border-radius: 23px;
  background: ${props => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  cursor: pointer;
`

const DoubleImageContainer = styled.div`
  position: relative;
  height: 375px;
  overflow: hidden;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    height: 488px;
  }
`

interface IDoubleImageProps {
  left?: boolean
}
const DoubledImage = styled.img<IDoubleImageProps>`
  position: absolute;
  top: 0;
  ${props => (props.left ? 'left: 40%;' : 'right: 40%;')}
  height: 350px;
  margin-bottom: 25px;
  @media (min-width: ${CUSTOM_BREAKPOINT}px) {
    height: 488px;
    ${props => (props.left ? 'left: 50px;' : 'right: 50px;')}
  }
`

const renderDoubleImage = () => (
  <DoubleImageContainer>
    <DoubledImage src={`${S3_TOUR_URL}/IMG_1408_framed.jpg`} />
    <DoubledImage
      src={`${S3_TOUR_URL}/smartmockups_ka4bbuua.png`}
      left
    />
  </DoubleImageContainer>
)

const renderDownloadButtons = () => (
  <>
    <Spacer size={26} />
    <DownloadText>Download It for Free</DownloadText>
    <Spacer size={10} />
    <DownloadButtonContainer>
      <a href="https://apps.apple.com/us/app/homelister-live-tours/id1515090384">
        <DownloadImage
          src={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/downloadAppStore.png`}
        />
      </a>
      <Spacer size={10} />
      <a href="https://play.google.com/store/apps/details?id=com.homelister&hl=en_US">
        <DownloadImage
          src={`${process.env.CDN_BASE_URL}/q_auto/amazon/assets/downloadPlayStore.png`}
        />
      </a>
    </DownloadButtonContainer>
  </>
)

const VirtualHomeToursPage = () => {
  // refs
  const ref = useRef<any>(null)

  // theme
  const theme = useContext(ThemeContext)

  const goNext = () => {
    if (ref.current !== null && ref.current.swiper !== null) ref.current.swiper.slideNext()
  }

  const goPrev = () => {
    if (ref.current !== null && ref.current.swiper !== null) ref.current.swiper.slidePrev()
  }

  const SwiperParams = {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    renderPrevButton: () => (
      <SwiperButtonContainer
        onClick={goPrev}
        left
      >
        <Circle>
          <LeftChevron
            height={15}
            fill={theme.colors.black}
          />
        </Circle>
      </SwiperButtonContainer>
    ),
    renderNextButton: () => (
      <SwiperButtonContainer onClick={goNext}>
        <Circle>
          <RightChevron
            height={15}
            fill={theme.colors.black}
          />
        </Circle>
      </SwiperButtonContainer>
    ),
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      dynamicBullets: false,
    },
  }

  return (
    <MaxWidthContainer>
      <Head>
        <title>Discover the Real Estate Live Tours App | HomeLister</title>
        <meta
          name="description"
          content="The new free HomeLister Live Tours app will let homeowners easily schedule and host live virtual house tours with buyers and their agents. Learn more today."
        />
        <meta
          name="keywords"
          content="Live Tour, Virtual Tours, App, iOS, Android, HomeLister"
        />
      </Head>
      <ExtraLargeHeader title="HomeLister Live Tours App" />
      <Spacer size={50} />
      <Row>
        <FlexItem>
          <SwiperContainer>
            <Swiper
              {...SwiperParams}
              ref={ref}
            >
              {renderDoubleImage()}
              <ImageCard imgSrc={`${S3_TOUR_URL}/smartmockups_ka4bcahr.jpg`} />
              <ImageCard imgSrc={`${S3_TOUR_URL}/smartmockups_ka4bcv4s.jpg`} />
            </Swiper>
          </SwiperContainer>
        </FlexItem>
        <FlexItem>
          <TextContainer>
            <StyledText>
              <StyledParagraph>
                The new free HomeLister Live Tours app will let homeowners easily schedule and host
                live virtual house tours with buyers and their agents.
              </StyledParagraph>
              <StyledParagraph>
                60% of showings now ask for a virtual tour before scheduling an in person tour - and
                now you can easily make those showings happen.
              </StyledParagraph>
              <StyledParagraph>
                The HomeLister Live Tours app makes it easy to do live virtual house tours and
                showings with the click of a button. Invite a buyer to your tour by entering their
                contact information, select a date and time, and you’re all set!&nbsp;
                <StyledAnchor href="http://insights.homelister.com/insights//homelister-app-instructions">
                  Click here
                </StyledAnchor>
                &nbsp;for a step by step instructional guide on how to use it.
              </StyledParagraph>
            </StyledText>
            {renderDownloadButtons()}
          </TextContainer>
        </FlexItem>
      </Row>
      <Spacer size={50} />
      <Row>
        <FlexItem>
          <Image imgSrc={`${S3_TOUR_URL}/smartmockups_ka4dc29g.jpg`} />
        </FlexItem>
        <FlexItem>
          <TextContainer>
            <RowHeader>Schedule a Virtual House Tour</RowHeader>
            <Spacer size={16} />
            <StyledText>
              <StyledParagraph>
                To schedule a virtual house tour with buyers and buyer agents, just enter the date
                and time and the attendee’s email or phone number. The app will automatically call
                you and the buyers at the right time and set up the video for you. All you have to
                do is talk! Schedule it with just one attendee or multiple attendees.
              </StyledParagraph>
            </StyledText>
          </TextContainer>
        </FlexItem>
      </Row>
      <Spacer size={50} />
      <Row reverse>
        <FlexItem>
          <TextContainer>
            <RowHeader>Host a Virtual House Tour</RowHeader>
            <Spacer size={16} />
            <StyledText>
              <StyledParagraph>
                Host virtual house tours through video conferencing technology without having to set
                up, maintain or understand video conferencing technology!
              </StyledParagraph>
            </StyledText>
          </TextContainer>
        </FlexItem>
        <FlexItem>
          <Image imgSrc={`${S3_TOUR_URL}/smartmockups_ka4d9xai.jpg`} />
        </FlexItem>
      </Row>
      <Spacer size={50} />
      <Row>
        <FlexItem>
          <Image imgSrc={`${S3_TOUR_URL}/smartmockups_ka4db760.jpg`} />
        </FlexItem>
        <FlexItem>
          <TextContainer>
            <RowHeader>Notifications</RowHeader>
            <Spacer size={16} />
            <StyledText>
              <StyledParagraph>
                Receive notifications of upcoming virtual house tours via text message or email.
              </StyledParagraph>
              <StyledParagraph>
                With the free HomeLister Live Tours app, you can schedule, host and manage your
                virtual house tours and reach more potential buyers and increase buyer and agent
                increase in your home.
              </StyledParagraph>
            </StyledText>
          </TextContainer>
        </FlexItem>
      </Row>
      <Spacer size={50} />
      <Row reverse>
        <FlexItem>
          <TextContainer>
            <RowHeader>Minimum Requirements</RowHeader>
            <Spacer size={16} />
            <StyledText>
              iPhone 5 and newer
              <br />
              iOS 10 and later
              <br />
              Android smartphones from 2015 and newer
              <br />
              Android 5 Lollipop and later
              <br />
              {renderDownloadButtons()}
            </StyledText>
          </TextContainer>
        </FlexItem>
        <FlexItem>{renderDoubleImage()}</FlexItem>
      </Row>
    </MaxWidthContainer>
  )
}

export default VirtualHomeToursPage
